import { supabase } from "@/integrations/supabase/client";
import {
  AMP_IMPORT_START,
  AMP_IMPORT_END,
  AMP_TEMPLATE,
  type AmpSectionDef,
  type AmpSourceMeta,
} from "./constants";
import { fetchAmpSourceDetailed } from "./prefill";

/** Ersätter tidigare importblock men lämnar egen text orörd. */
export function mergeImportedBody(existing: string | null | undefined, imported: string, label: string): string {
  const own = stripImportBlock(existing ?? "").trim();
  const block = `${AMP_IMPORT_START} – ${label}\n${imported}\n${AMP_IMPORT_END}`;
  return own ? `${own}\n\n${block}` : block;
}

export function stripImportBlock(body: string): string {
  const start = body.indexOf(AMP_IMPORT_START);
  if (start === -1) return body;
  const end = body.indexOf(AMP_IMPORT_END, start);
  if (end === -1) return body.slice(0, start);
  return body.slice(0, start) + body.slice(end + AMP_IMPORT_END.length);
}

export function extractImportBlock(body: string | null | undefined): string | null {
  if (!body) return null;
  const start = body.indexOf(AMP_IMPORT_START);
  if (start === -1) return null;
  const end = body.indexOf(AMP_IMPORT_END, start);
  const inner = body.slice(start, end === -1 ? undefined : end + AMP_IMPORT_END.length);
  return inner.trim() || null;
}

export function docLabel(def?: AmpSectionDef): string {
  if (!def?.docRefs?.length) return "Bas-processen";
  return def.docRefs.map((r) => `Dok ${r.doc} ${r.title}`).join(", ");
}

export interface RefreshResult {
  body: string;
  status: "not_started" | "in_progress" | "done" | "not_applicable" | null;
  source_meta: AmpSourceMeta;
  found: boolean;
  structured_data?: Record<string, any>;
}

/** Hämtar underlag för ett avsnitt och returnerar den uppdaterade texten + metadata. */
export async function buildSectionImport(
  def: AmpSectionDef,
  projectId: string,
  currentBody: string | null,
  currentStatus: string,
  currentStructuredData: Record<string, any> = {}
): Promise<RefreshResult | null> {
  if (!def.source) return null;
  const result = await fetchAmpSourceDetailed(def.source, projectId);
  const label = docLabel(def);
  const meta: AmpSourceMeta = {
    doc: def.docRefs?.[0]?.doc,
    docTitle: def.docRefs?.[0]?.title,
    count: result.count,
    fetchedAt: new Date().toISOString(),
    sourceUpdatedAt: result.sourceUpdatedAt,
  };

  if (!result.text) {
    return { body: currentBody ?? "", status: null, source_meta: meta, found: false };
  }

  const structured_data = result.structuredData
    ? { ...currentStructuredData, ...result.structuredData }
    : undefined;

  return {
    body: mergeImportedBody(currentBody, result.text, label),
    status: currentStatus === "not_started" ? "in_progress" : null,
    source_meta: meta,
    found: true,
    structured_data,
  };
}

/** Kör förifyllning för alla avsnitt i en nyskapad AMP. */
export async function prefillPlan(planId: string, projectId: string, userId?: string | null) {
  const { data: sections } = await supabase
    .from("amp_sections")
    .select("id, section_key, body, status, structured_data")
    .eq("plan_id", planId);

  for (const section of sections || []) {
    const def = AMP_TEMPLATE.find((d) => d.key === section.section_key);
    if (!def?.source) continue;
    const currentStructuredData =
      (section.structured_data as Record<string, any>) || {};
    const result = await buildSectionImport(
      def,
      projectId,
      section.body,
      section.status,
      currentStructuredData
    );
    if (!result) continue;
    const patch: any = {
      source_meta: result.source_meta,
      updated_by: userId ?? null,
    };
    if (result.found) {
      patch.body = result.body;
      if (result.status) patch.status = result.status;
      if (result.structured_data) patch.structured_data = result.structured_data;
    }
    await supabase.from("amp_sections").update(patch).eq("id", section.id);
  }
}
